import React, { useEffect, useRef, useState } from "react";
import styles from "./orders.module.scss";
import { useTranslation } from "react-i18next";
import Order from "./order/Order";
import { v4 as uuidv4 } from "uuid";
import {
  IStrategiesInfo,
  IToOrder,
  OpenedPositions,
  StateOfTrade,
  TimeFrames,
  TradeAction,
} from "../../../shared/types";
import { useRequest } from "../../../hooks/request.hook";
import { message as messageAnt } from "antd";
import { CountryExchange } from "../../../shared/countryExchange";
import dayjs from "dayjs";
import { usePositions } from "../../../hooks/positions.hook";
import soundOn from "../../../assets/images/soundOn.png";
import soundOff from "../../../assets/images/soundOff.png";
import freeSignalOn from "../../../assets/images/freeSignalOn.png";
import freeSignalOff from "../../../assets/images/freeSignalOff.png";
import { useAdvisors } from "../../../hooks/advisor.hook";
import { useOpenedFreeSignals } from "../../../hooks/openedFreeSignals.hook";

interface IProps {
  varForUpdate: string;
  country: CountryExchange | undefined;
  strategiesInfo: IStrategiesInfo | undefined;
}

const Orders = ({ varForUpdate, country, strategiesInfo }: IProps) => {
  const { t, i18n } = useTranslation();

  const { loading, getStatusNotificationsToCustomers, setStatusNotificationsToCustomers, error, clearError } =
    useAdvisors();

  const {
    loading: loadingRequest,
    getAllEnteredRequestsFromAdvisors,
    removeEnteredRequest,
    error: errorRequest,
    clearError: clearErrorRequest,
  } = useRequest();
  const {
    loading: loadingPositions,
    getAllPositionsFromAdvisors,
    removeOpenedPositions,
    saveOpenedPositions,
    sendSignalToChannel,
    sendSignalToFreeChannel,
    error: errorPositions,
    clearError: clearErrorPositions,
  } = usePositions();
  const {
    loading: loadingOpenedFreeSignals,
    getAllOpenedFreeSignals,
    saveOpenedFreeSignal,
    removeOpenedFreeSignal,
    error: errorOpenedFreeSignals,
    clearError: clearErrorOpenedFreeSignals,
  } = useOpenedFreeSignals();

  const blockDoubleRegisterRef = useRef(false); //блокировка случайного двойного нажатия на регистрацию или отмену заявки пока не прошла обработка запроса

  const [toOrderList, setToOrderList] = useState<IToOrder[]>([]);
  const [openedPositions, setOpenedPositions] = useState<typeof OpenedPositions>();
  const [openedFreeSignals, setOpenedFreeSignal] = useState<string[]>([]);
  const [btnOrderLoading, setBtnOrderLoading] = useState(false);
  const [stateNotificationsTrigger, setStateNotificationsTrigger] = useState<boolean>(); //выключатель уведомлений о моих сигналах у подписчиков
  const [stateFreeSignal, setStateFreeSignal] = useState<boolean>(false); //выключатель что чигнал бесплатный, если да, то отправляем и в бесплатный чат с сигналами

  //включение/выключение уведомлений о моих сигналах у подписчиков
  const switchNotificationsHandler = async () => {
    const stateNotifications = stateNotificationsTrigger;
    try {
      const dataStatus = await setStatusNotificationsToCustomers(!stateNotifications);
      setStateNotificationsTrigger(!stateNotifications);
      messageAnt.info(dataStatus.message);
      if (stateNotifications) {
        setStateFreeSignal(false);
      }
    } catch (e) {
      if (e instanceof Error) {
        messageAnt.error(e.message);
      }
      clearError();
    }
  };

  //включение/выключение статуса сигнала платный/бесплатный
  const switchStateFreeSignalHandler = async () => {
    const stateFree = stateFreeSignal;
    setStateFreeSignal(!stateFree);
  };

  //функция получение статуса вкл/выкл уведомлений о моих сигналах у подписчиков
  const getStatusNotificationsTrigger = async () => {
    try {
      const data = await getStatusNotificationsToCustomers();
      messageAnt.info(data.message);
      return data.info;
    } catch (e) {
      messageAnt.info(error);
      clearError();
    }
  };

  //изначальное получение статуса вкл/выкл уведомлений о моих сигналах у подписчиков
  useEffect(() => {
    const get = async () => {
      const status = await getStatusNotificationsTrigger();
      setStateNotificationsTrigger(status);
    };
    get();
  }, []);

  //получение данных по выставленным заявкам из скрипта советника на сервере
  const getEnteredRequests = async () => {
    try {
      const data = await getAllEnteredRequestsFromAdvisors();
      messageAnt.info(data.message);
      return data.info;
    } catch (e) {
      messageAnt.info(errorRequest);
      clearErrorRequest();
      return [];
    }
  };

  //изначальное получение данных по выставленным заявкам
  useEffect(() => {
    if (varForUpdate) {
      const get = async () => {
        const enteredRequests = await getEnteredRequests();
        setToOrderList(enteredRequests);
      };
      get();
    }
  }, [varForUpdate]);

  //функция получения данных по тикерам, по которым есть открытые бесплатные сигналы
  const getOpenedFreeSignalRequests = async (country: CountryExchange) => {
    try {
      const data = await getAllOpenedFreeSignals(country);
      messageAnt.info(data.message);
      return data.info;
    } catch (e) {
      messageAnt.info(errorRequest);
      clearErrorRequest();
      return [];
    }
  };

  //изначальное получение данных по тикерам, по которым есть открытые бесплатные сигналы
  useEffect(() => {
    if (varForUpdate) {
      const get = async () => {
        if (country) {
          const openedFreeSignals = await getOpenedFreeSignalRequests(country);
          if (openedFreeSignals && openedFreeSignals.length !== 0) {
            const opened = openedFreeSignals.map(elem => elem.ticker);
            setOpenedFreeSignal(opened);
          }
        }
      };
      get();
    }
  }, [varForUpdate, country]);

  //получение данных по открытым позициям для всех стратегий, таймфреймов и инструментов из скрипта советника на сервере
  const getOpenedPositions = async () => {
    try {
      const data = await getAllPositionsFromAdvisors();
      messageAnt.info(data.message);
      return data.info;
    } catch (e) {
      messageAnt.info(errorPositions);
      clearErrorPositions();
      return undefined;
    }
  };

  //изначальное получение данных по открытым позициям
  useEffect(() => {
    if (varForUpdate) {
      const get = async () => {
        const openedPositions = await getOpenedPositions();
        setOpenedPositions(openedPositions);
      };
      get();
    }
  }, [varForUpdate]);

  //функция для сохранение данных по открытым позициям в БД для одной стратегии в которой были изменения по позициям по любому тикеру и тф
  const savePositions = async (country: CountryExchange, strategy: string, positions: string) => {
    if (!positions) {
      //если последняя позиция по стратегии закрыта, то очищаем документ по этой стратегии в БД
      try {
        const data = await removeOpenedPositions(country, strategy);
        messageAnt.info(data.message);
      } catch (e) {
        messageAnt.info(errorPositions);
        clearErrorPositions();
      }
    } else {
      try {
        const data = await saveOpenedPositions(country, strategy, positions);
        messageAnt.info(data.message);
      } catch (e) {
        messageAnt.info(errorPositions);
        clearErrorPositions();
      }
    }
  };

  //функция для отправки сообщения о моей сделке пользователям в телеграм
  const sendSignalToCustomers = async (order: IToOrder) => {
    try {
      const data = await sendSignalToChannel(order);
      messageAnt.info(data.message);
    } catch (e) {
      messageAnt.info(errorPositions);
      clearErrorPositions();
    }
  };

  //функция для отправки сообщения о моей сделке пользователям в бесплатный телеграм канал
  const sendFreeSignalToCustomers = async (order: IToOrder) => {
    try {
      const data = await sendSignalToFreeChannel(order);
      messageAnt.info(data.message);
    } catch (e) {
      messageAnt.info(errorPositions);
      clearErrorPositions();
    }
  };

  //оформление сделок
  const registerPositionHandler = async (order: IToOrder) => {
    if (!blockDoubleRegisterRef.current) {
      blockDoubleRegisterRef.current = true;

      //индекс заявки, по которой оформили сделку, в списке заявок
      const indexOfOrderInList = toOrderList.findIndex(ord => {
        if (ord.strategy === order.strategy && ord.ticker === order.ticker && ord.timeframe === order.timeframe) {
          return true;
        }
      });

      //обновим в локальном списке заявок цену в обработываемой заявке, чисто для того, чтобы в момент нажатия на кнопку при перерендере списка не отображалась снова старая цена заявки
      if (indexOfOrderInList !== -1) {
        if (country) {
          const newToOrderList = [...toOrderList];
          newToOrderList[indexOfOrderInList].price = order.price;
          setToOrderList(newToOrderList);
        }
      }

      //если по каким-то причинам индекс не найден, то это ошибка, ничего делать не нужно
      if (indexOfOrderInList !== -1) {
        if (country) {
          setBtnOrderLoading(true);
          let data;
          if (order.actionType === TradeAction.BUY && order.action === StateOfTrade.NEED_OPEN_SERIES) {
            data = {
              enters: [
                {
                  price: order.price,
                  stopLoss: order.stopLoss,
                  takeProfit: order.takeProfit,
                  date: dayjs(order.date).set("second", 0).format("YYYY-MM-DD HH:mm:ss"),
                  value: order.value,
                  specificity: order.specificity,
                },
              ],
              exits: [],
            };
          } else if (
            order.actionType === TradeAction.BUY &&
            order.action === StateOfTrade.NEED_ADD_ENTER_IN_SERIES &&
            openedPositions
          ) {
            const exits = [
              ...JSON.parse(
                JSON.stringify(openedPositions[order.strategy][order.ticker][order.timeframe as TimeFrames].exits)
              ),
            ];
            const enters = [
              ...JSON.parse(
                JSON.stringify(openedPositions[order.strategy][order.ticker][order.timeframe as TimeFrames].enters)
              ),
            ];
            enters.push({
              price: order.price,
              stopLoss: order.stopLoss,
              takeProfit: order.takeProfit,
              date: dayjs(order.date).set("second", 0).format("YYYY-MM-DD HH:mm:ss"),
              value: order.value,
              specificity: order.specificity,
            });
            data = { enters: enters, exits: exits };
          } else if (
            order.actionType === TradeAction.SELL &&
            order.action === StateOfTrade.NEED_ADD_EXIT_IN_SERIES &&
            openedPositions
          ) {
            const enters = [
              ...JSON.parse(
                JSON.stringify(openedPositions[order.strategy][order.ticker][order.timeframe as TimeFrames].enters)
              ),
            ];
            const exits = [
              ...JSON.parse(
                JSON.stringify(openedPositions[order.strategy][order.ticker][order.timeframe as TimeFrames].exits)
              ),
            ];
            exits.push({
              price: order.price,
              date: dayjs(order.date).set("second", 0).format("YYYY-MM-DD HH:mm:ss"),
              value: order.value,
              specificity: order.specificity,
            });
            data = { enters: enters, exits: exits };
          } else if (order.actionType === TradeAction.SELL && order.action === StateOfTrade.NEED_CLOSE_SERIES) {
            data = null;
          }

          let newOpenedPositions;
          if (openedPositions) {
            newOpenedPositions = JSON.parse(JSON.stringify(openedPositions));
            if (data) {
              if (newOpenedPositions[order.strategy]) {
                if (newOpenedPositions[order.strategy][order.ticker]) {
                  if (newOpenedPositions[order.strategy][order.ticker][order.timeframe]) {
                    newOpenedPositions[order.strategy][order.ticker][order.timeframe] = data;
                  } else {
                    newOpenedPositions[order.strategy][order.ticker][order.timeframe] = data;
                  }
                } else {
                  newOpenedPositions[order.strategy][order.ticker] = { [order.timeframe]: data };
                }
              } else {
                newOpenedPositions[order.strategy] = { [order.ticker]: { [order.timeframe]: data } };
              }
            } else {
              delete newOpenedPositions[order.strategy][order.ticker][order.timeframe];
              if (Object.keys(newOpenedPositions[order.strategy][order.ticker]).length === 0) {
                delete newOpenedPositions[order.strategy][order.ticker];
                if (Object.keys(newOpenedPositions[order.strategy]).length === 0) {
                  delete newOpenedPositions[order.strategy];
                }
              }
            }
          } else {
            newOpenedPositions = { [order.strategy]: { [order.ticker]: { [order.timeframe]: data } } };
          }

          try {
            //сохранение обновленной информации по открытым позициям в БД и запрос на отправку сигнала в чат для пользователей и если сигнал бесплатный, то и в бесплатный чат с сигналами, и запрос на генерацию видео карточек сигнала
            if (country) {
              await savePositions(country, order.strategy, JSON.stringify(newOpenedPositions[order.strategy]));
              await sendSignalToCustomers(order);
              if (stateFreeSignal) {
                await sendFreeSignalToCustomers(order);
                setStateFreeSignal(false);
                //если это фри сигнал именно на открытие позиции, то заносим тикер в реестр открытых фри сигналов, чтобы потом напоминать когда будет сигнал на добавление и закрытие, чтобы тоже отправить фри сигнал
                if (order.action === StateOfTrade.NEED_OPEN_SERIES) {
                  await saveOpenedFreeSignal(country, order.ticker);
                } else if (order.action === StateOfTrade.NEED_CLOSE_SERIES) {
                  //а если на закрытие, то исключаем из реестра
                  await removeOpenedFreeSignal(country, order.ticker);
                }
              }
              setOpenedPositions(newOpenedPositions);
            }
          } catch (e) {}

          //убираем заявку из списка, по которой оформили сделку
          if (indexOfOrderInList !== -1) {
            const newToOrderList = [...toOrderList];
            const deletedOrders = newToOrderList.splice(indexOfOrderInList, 1);
            await removeEnteredRequest(country, deletedOrders[0]);
            setToOrderList(newToOrderList);
          }
          setBtnOrderLoading(false);
        }
      }

      blockDoubleRegisterRef.current = false;
    }
  };

  //отмена заявки
  const cancelOrderHandler = async (strategy: string, ticker: string, timeframe: string) => {
    if (!blockDoubleRegisterRef.current) {
      blockDoubleRegisterRef.current = true;

      setBtnOrderLoading(true);
      const deleteIndex = toOrderList.findIndex(order => {
        if (order.strategy === strategy && order.ticker === ticker && order.timeframe === timeframe) {
          return true;
        }
      });
      if (deleteIndex !== -1) {
        if (country) {
          const newToOrderList = [...toOrderList];
          const deletedOrders = newToOrderList.splice(deleteIndex, 1);
          await removeEnteredRequest(country, deletedOrders[0]);
          setToOrderList(newToOrderList);
        }
      }
      setBtnOrderLoading(false);

      blockDoubleRegisterRef.current = false;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.blockWrap}>
          {/*//@ts-ignore*/}
          <p className={styles.title}>{t("AdvisorsSubmittedApplicationsTitle")}</p>
          <div className={styles.switches}>
            <div className={styles.switchSound} onClick={switchNotificationsHandler}>
              {stateNotificationsTrigger ? <img src={soundOn} alt="" /> : <img src={soundOff} alt="" />}
              {/*//@ts-ignore*/}
              <p>{t("SignalsToCustomers")}</p>
            </div>
            <div className={styles.switchSound} onClick={switchStateFreeSignalHandler}>
              {stateFreeSignal ? <img src={freeSignalOn} alt="" /> : <img src={freeSignalOff} alt="" />}
              {/*//@ts-ignore*/}
              <p>{t("FreeSignal")}</p>
            </div>
          </div>
          {toOrderList && toOrderList.length !== 0 && (
            <div className={styles.toOrdWrap}>
              {toOrderList.map((toOrder, index) => (
                <Order
                  key={uuidv4()}
                  strategiesInfo={strategiesInfo}
                  toOrder={toOrder}
                  btnOrderLoading={btnOrderLoading}
                  registerPositionHandler={registerPositionHandler}
                  cancelOrderHandler={cancelOrderHandler}
                  openedFreeSignals={openedFreeSignals}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Orders;
